import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiSearch, FiMapPin, FiStar, FiFilter, FiGrid, FiList } from 'react-icons/fi';
import Button from '../../components/UI/Button';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

const ParksContainer = styled.div`
  min-height: 100vh;
  padding: var(--space-2xl) 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--space-lg);
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: var(--space-3xl);
  
  h1 {
    font-size: 3rem;
    color: var(--neutral-gray-900);
    margin-bottom: var(--space-md);
    
    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }
  
  p {
    font-size: 1.25rem;
    color: var(--neutral-gray-600);
    max-width: 600px;
    margin: 0 auto;
  }
`;

const FiltersBar = styled.div`
  background: white;
  border-radius: var(--radius-xl);
  padding: var(--space-lg);
  box-shadow: var(--shadow-md);
  margin-bottom: var(--space-2xl);
  display: flex;
  flex-direction: column;
  gap: var(--space-lg);
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const SearchSection = styled.div`
  display: flex;
  gap: var(--space-md);
  flex: 1;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SearchInput = styled.div`
  position: relative;
  flex: 1;
  
  input {
    width: 100%;
    padding: var(--space-md) var(--space-md) var(--space-md) 3rem;
    border: 1px solid var(--neutral-gray-300);
    border-radius: var(--radius-lg);
    font-size: 1rem;
    
    &:focus {
      border-color: var(--primary-green);
      box-shadow: 0 0 0 3px rgba(46, 139, 87, 0.1);
    }
  }
  
  .search-icon {
    position: absolute;
    left: var(--space-md);
    top: 50%;
    transform: translateY(-50%);
    color: var(--neutral-gray-400);
  }
`;

const FilterSelect = styled.select`
  padding: var(--space-md);
  border: 1px solid var(--neutral-gray-300);
  border-radius: var(--radius-lg);
  font-size: 1rem;
  min-width: 150px;
  
  &:focus {
    border-color: var(--primary-green);
    box-shadow: 0 0 0 3px rgba(46, 139, 87, 0.1);
  }
`;

const ViewToggle = styled.div`
  display: flex;
  background: var(--neutral-gray-100);
  border-radius: var(--radius-lg);
  padding: var(--space-xs);
`;

const ViewButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-sm) var(--space-md);
  border: none;
  background: ${props => props.active ? 'white' : 'transparent'};
  color: ${props => props.active ? 'var(--primary-green)' : 'var(--neutral-gray-600)'};
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: var(--transition-base);
  box-shadow: ${props => props.active ? 'var(--shadow-sm)' : 'none'};
  
  &:hover {
    background: ${props => props.active ? 'white' : 'var(--neutral-gray-200)'};
  }
`;

const ParksGrid = styled.div`
  display: grid;
  grid-template-columns: ${props => props.viewMode === 'grid' 
    ? 'repeat(auto-fill, minmax(350px, 1fr))' 
    : '1fr'};
  gap: var(--space-xl);
`;

const ParkCard = styled(motion.div)`
  background: white;
  border-radius: var(--radius-2xl);
  overflow: hidden;
  box-shadow: var(--shadow-lg);
  transition: var(--transition-base);
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-xl);
  }
  
  ${props => props.viewMode === 'list' && `
    display: flex;
    align-items: center;
    
    @media (max-width: 768px) {
      flex-direction: column;
    }
  `}
`;

const ParkImage = styled.div`
  height: ${props => props.viewMode === 'list' ? '200px' : '250px'};
  width: ${props => props.viewMode === 'list' ? '300px' : '100%'};
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  position: relative;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 100%;
    height: 200px;
  }
`;

const ParkBadge = styled.div`
  position: absolute;
  top: var(--space-md);
  left: var(--space-md);
  background: ${props => props.type === 'national' 
    ? 'linear-gradient(135deg, var(--primary-green), var(--primary-green-dark))' 
    : 'linear-gradient(135deg, var(--secondary-blue), var(--primary-green))'};
  color: white;
  padding: var(--space-sm) var(--space-md);
  border-radius: var(--radius-lg);
  font-size: 0.875rem;
  font-weight: 600;
`;

const ParkContent = styled.div`
  padding: var(--space-xl);
  flex: 1;
  
  h3 {
    font-size: 1.5rem;
    color: var(--neutral-gray-900);
    margin-bottom: var(--space-sm);
  }
  
  .location {
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    color: var(--neutral-gray-600);
    margin-bottom: var(--space-md);
    font-weight: 500;
  }
  
  .description {
    color: var(--neutral-gray-600);
    line-height: 1.6;
    margin-bottom: var(--space-lg);
  }
  
  .rating {
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    margin-bottom: var(--space-lg);
    
    .stars {
      display: flex;
      gap: var(--space-xs);
      color: #FFD700;
    }
    
    .rating-text {
      color: var(--neutral-gray-600);
      font-size: 0.875rem;
    }
  }
`;

const ParkActions = styled.div`
  display: flex;
  gap: var(--space-md);
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const ResultsInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-xl);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: var(--space-md);
    align-items: flex-start;
  }
`;

const Parks = () => {
  const [parks, setParks] = useState([]);
  const [filteredParks, setFilteredParks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedState, setSelectedState] = useState('all');
  const [viewMode, setViewMode] = useState('grid');

  // Mock parks data
  const mockParks = [
    {
      id: 1,
      name: "Yellowstone National Park",
      state: "Wyoming",
      type: "national",
      image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      description: "America's first national park, known for its geothermal features, wildlife, and stunning landscapes.",
      rating: 4.9,
      reviews: 1250,
      visited: true
    },
    {
      id: 2,
      name: "Great Smoky Mountains National Park",
      state: "Tennessee",
      type: "national",
      image: "https://images.unsplash.com/photo-1571424750519-f2a44e0b7d0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      description: "Known for its diverse wildlife, ancient mountains, and remnants of Southern Appalachian culture.",
      rating: 4.8,
      reviews: 980,
      visited: true
    },
    {
      id: 3,
      name: "Goblin Valley State Park",
      state: "Utah",
      type: "state",
      image: "https://images.unsplash.com/photo-1575408264798-b50b252663e6?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      description: "Unique red rock formations creating a landscape of mushroom-shaped rock pinnacles called goblins.",
      rating: 4.7,
      reviews: 432,
      visited: true
    },
    {
      id: 4,
      name: "Zion National Park",
      state: "Utah",
      type: "national",
      image: "https://images.unsplash.com/photo-1605538883669-825200433431?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      description: "Famous for its towering sandstone cliffs, narrow slot canyons, and diverse desert ecosystem.",
      rating: 4.9,
      reviews: 1650,
      visited: false
    },
    {
      id: 5,
      name: "Lake Tahoe Nevada State Park",
      state: "Nevada",
      type: "state",
      image: "https://images.unsplash.com/photo-1541513354703-33425f5ba0bf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      description: "Crystal clear alpine lake surrounded by mountains, perfect for outdoor recreation year-round.",
      rating: 4.6,
      reviews: 675,
      visited: true
    },
    {
      id: 6,
      name: "Arches National Park",
      state: "Utah",
      type: "national",
      image: "https://images.unsplash.com/photo-1539650116574-75c0c6d0db3e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      description: "Home to over 2,000 natural stone arches, balanced rocks, and stunning red rock landscapes.",
      rating: 4.8,
      reviews: 1120,
      visited: false
    }
  ];

  useEffect(() => {
    // Simulate API call
    const loadParks = async () => {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setParks(mockParks);
      setFilteredParks(mockParks);
      setLoading(false);
    };

    loadParks();
  }, []);

  useEffect(() => {
    let filtered = parks;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(park =>
        park.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        park.state.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by type
    if (selectedType !== 'all') {
      filtered = filtered.filter(park => park.type === selectedType);
    }

    // Filter by state
    if (selectedState !== 'all') {
      filtered = filtered.filter(park => park.state === selectedState);
    }

    setFilteredParks(filtered);
  }, [searchTerm, selectedType, selectedState, parks]);

  const states = [...new Set(parks.map(park => park.state))].sort();

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    const stars = [];

    for (let i = 0; i < fullStars; i++) {
      stars.push(<FiStar key={i} fill="currentColor" />);
    }

    if (hasHalfStar) {
      stars.push(<FiStar key="half" style={{ opacity: 0.5 }} />);
    }

    return stars;
  };

  if (loading) {
    return <LoadingSpinner text="Loading parks..." />;
  }

  return (
    <ParksContainer>
      <Container>
        <PageHeader>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            State & National Parks
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Discover the incredible destinations we've explored and get inspired 
            for your next adventure in America's most beautiful parks.
          </motion.p>
        </PageHeader>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <FiltersBar>
            <SearchSection>
              <SearchInput>
                <FiSearch className="search-icon" size={20} />
                <input
                  type="text"
                  placeholder="Search parks by name or state..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </SearchInput>
              
              <FilterSelect
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
              >
                <option value="all">All Parks</option>
                <option value="national">National Parks</option>
                <option value="state">State Parks</option>
              </FilterSelect>

              <FilterSelect
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
              >
                <option value="all">All States</option>
                {states.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </FilterSelect>
            </SearchSection>

            <ViewToggle>
              <ViewButton
                active={viewMode === 'grid'}
                onClick={() => setViewMode('grid')}
              >
                <FiGrid size={18} />
              </ViewButton>
              <ViewButton
                active={viewMode === 'list'}
                onClick={() => setViewMode('list')}
              >
                <FiList size={18} />
              </ViewButton>
            </ViewToggle>
          </FiltersBar>

          <ResultsInfo>
            <p>
              Showing {filteredParks.length} of {parks.length} parks
            </p>
          </ResultsInfo>

          <ParksGrid viewMode={viewMode}>
            {filteredParks.map((park, index) => (
              <ParkCard
                key={park.id}
                viewMode={viewMode}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <ParkImage image={park.image} viewMode={viewMode}>
                  <ParkBadge type={park.type}>
                    {park.type === 'national' ? 'National Park' : 'State Park'}
                  </ParkBadge>
                </ParkImage>
                
                <ParkContent>
                  <h3>{park.name}</h3>
                  
                  <div className="location">
                    <FiMapPin size={16} />
                    {park.state}
                  </div>
                  
                  <p className="description">{park.description}</p>
                  
                  <div className="rating">
                    <div className="stars">
                      {renderStars(park.rating)}
                    </div>
                    <span className="rating-text">
                      {park.rating} ({park.reviews} reviews)
                    </span>
                  </div>
                  
                  <ParkActions>
                    <Button as={Link} to={`/parks/${park.id}`}>
                      View Details
                    </Button>
                    {park.visited && (
                      <Button variant="outline" as={Link} to={`/gallery?park=${park.id}`}>
                        View Photos
                      </Button>
                    )}
                  </ParkActions>
                </ParkContent>
              </ParkCard>
            ))}
          </ParksGrid>

          {filteredParks.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{ textAlign: 'center', padding: 'var(--space-3xl)' }}
            >
              <h3>No parks found</h3>
              <p>Try adjusting your search criteria to find more parks.</p>
            </motion.div>
          )}
        </motion.div>
      </Container>
    </ParksContainer>
  );
};

export default Parks;
